package com.swetdot.swetgram.ui.discover

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import com.swetdot.swetgram.ui.design.SwetBasicTextField
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.window.Dialog
import com.swetdot.swetgram.data.moderation.ReportTarget
import com.swetdot.swetgram.domain.DiscoverComment
import com.swetdot.swetgram.domain.DiscoverContent
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetSendButton
import com.swetdot.swetgram.ui.design.SwetTextButton
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.design.swetAnimateItem
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.design.swetSheetDialogProperties
import com.swetdot.swetgram.ui.moderation.ReportRequest
import com.swetdot.swetgram.ui.moderation.ReportedAccount
import com.swetdot.swetgram.ui.profile.ProfileConfirmDialog
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.comments_close
import swetgram.shared.generated.resources.comments_title
import swetgram.shared.generated.resources.discover_groups_add_comment
import swetgram.shared.generated.resources.discover_groups_cancel
import swetgram.shared.generated.resources.discover_groups_delete
import swetgram.shared.generated.resources.discover_groups_delete_comment_confirm_body
import swetgram.shared.generated.resources.discover_groups_delete_comment_confirm_title
import swetgram.shared.generated.resources.discover_groups_edit
import swetgram.shared.generated.resources.discover_groups_post_unavailable_body
import swetgram.shared.generated.resources.discover_groups_post_unavailable_title
import swetgram.shared.generated.resources.discover_groups_publish
import swetgram.shared.generated.resources.discover_groups_report
import swetgram.shared.generated.resources.discover_groups_replying_to
import swetgram.shared.generated.resources.discover_groups_save
import swetgram.shared.generated.resources.discover_groups_thread_empty_body
import swetgram.shared.generated.resources.discover_groups_thread_empty_title
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * The comment thread as a BOTTOM SHEET over the feed, drawn to the user's mockup.
 *
 * THE USER'S INSTRUCTION IS THE HEADLINE. Comments used to be `PostDetailScreen`, a pushed route:
 * tapping the comment count on a feed card replaced the feed with a second screen carrying its own
 * copy of the post. They are a sheet now. The feed stays where it was, dimmed behind a scrim, and
 * the thread rises over the bottom of it. Nothing about the post is redrawn inside the sheet,
 * because the card the reader tapped is still on screen four dp behind it.
 *
 *
 * WHAT THIS FILE IS FOR, AND THE DEFECT IT IS SHAPED AROUND
 *
 * A sheet that re-implemented the thread would be the SECOND implementation of one screen, which is
 * the duplicate-rule defect this codebase has spent the week deleting: two renderings of one list
 * drift silently, and the drift is invisible until a Russian reader meets one of them. So the thread
 * body is written ONCE, here, as [commentThreadItems] and [CommentComposerBar], and both surfaces
 * render it.
 *
 * `PostDetailScreen` DRAWS THE SAME BODY. Its private comment items, its private `Composer` and its
 * private `withinLimit` are deleted; it calls [commentThreadItems] and [CommentComposerBar] and the
 * view model's `withinDraftLimit`. There is exactly one rendering of a comment thread in this app,
 * which is the whole reason this file is shaped as a `LazyListScope` extension rather than a screen.
 *
 * THE ROUTE SURVIVES, and retiring it was considered and refused. Its APP BAR is the only place in
 * the app that carries Edit post and Delete post: `DiscoverPostCard`'s own note records that the
 * feed card deliberately carries neither. Retiring the route would therefore delete a post author's
 * ability to edit or delete their own post from the client entirely, which is not a presentation
 * change.
 *
 * WHAT REACHES IT, after the wiring: the feed ROW's own tap, while the comment COUNT beside it opens
 * this sheet. `DiscoverPostRow` takes those as two lambdas rather than one -- see its `onOpenPost`
 * and `onOpenComments` -- because they are two different requests that happened to share a handler.
 * The control labelled "Comments" opens the comments; the post opens the post. That split is what
 * kept both doors open without inventing an "Open post" menu entry and its two translations.
 *
 *
 * WHAT THE MOCKUP ASKS FOR, AND HOW IT IS IMPLEMENTED
 *
 *  * «ОТВЕТИТЬ» -- REPLY. Replies remain in the contract's flat chronological list and address the
 *    selected author through a server-resolved @mention. This gives the recipient a real Activity
 *    event without inventing a parent id or presenting a threaded hierarchy the API does not store.
 *  * THE SECOND HEART. The mockup draws a heart with a count under the comment text AND an outlined
 *    heart at the far right of the same row. Both are `setCommentLiked` -- there is exactly one like
 *    operation per comment -- so they are ONE control drawn twice, not two controls. Drawing it
 *    twice would give a row two hearts that must always agree, which is a state-synchronisation bug
 *    waiting for the first dropped response, and would make a screen reader announce the same action
 *    twice per comment. ONE heart is drawn, at the FAR RIGHT, with its count beneath it -- the
 *    mockup's own position for the control it calls "the like control itself", and the position
 *    with room for §6.3's 44dp target away from the text. See `DiscoverLikeButton(stacked = true)`.
 *  * «1 ч.» / «45 мин.» -- A RELATIVE DURATION ON EACH COMMENT. NOT DRAWN. This app has ONE
 *    relative-date rule -- `dayLabel` to `DayLabel` to `dayLabelText`, giving today, yesterday, a
 *    weekday abbreviation and then an explicit date -- and `discoverTimestamp` is its Discover call
 *    site. A per-comment "1 h" would be a SECOND date rule, in a codebase whose date rules were
 *    deliberately collapsed into one three commits ago, and it would need its own plural forms in
 *    Russian to say so. The row keeps `discoverTimestamp`.
 *
 *
 * WHAT SURVIVES FROM THE ROUTE, BECAUSE DROPPING IT WOULD BE A REGRESSION RATHER THAN A RESKIN
 *
 * The composer and its length rule (both counts, for `DraftState`'s reason), the author's Edit and
 * Delete with their confirmations, the Report action, cursor paging, the error banner ABOVE the
 * content rather than instead of it, the freshness admission, and the single sentence for a post
 * that is gone -- deleted, never existed, in a group the reader left, or written by somebody they
 * blocked -- which stays one sentence because the server answers one 404 for all four.
 *
 *
 * DISMISSAL, AND WHY IT IS A `Dialog`
 *
 * Three ways out, all of them required: the ✕ in the header, a tap on the scrim, and the back
 * gesture. A `Dialog` with `usePlatformDefaultWidth = false` gives the scrim and the back gesture
 * for free on both platforms, which a hand-rolled overlay in the host's composition would not: back
 * would need a `BackHandler` this module does not otherwise depend on, and the scrim would need to
 * be painted and hit-tested by hand. It is the surface `ChatDialog`, `ProfileDialog` and
 * `ReportSheet` already use, anchored to the bottom instead of centred.
 *
 * DISMISSAL IS NEVER REFUSED, unlike `ReportSheet`'s. Nothing here is unrecoverable: the draft lives
 * in `PostDetailUiState.draft` rather than in this composition, so closing the sheet mid-sentence
 * and reopening it finds the words still there. That is also what makes the sheet survive rotation
 * -- see [CommentsSheet]'s note on the view model's scope, which is the one thing the host must get
 * right.
 */
@Composable
fun CommentsSheet(
    state: PostDetailUiState,
    /**
     * Closes the sheet. Invoked by the ✕, by a scrim tap and by the back gesture alike.
     *
     * THE HOST MUST NOT DESTROY THE VIEW MODEL HERE. `PostDetailViewModel` holds the draft, and it
     * has to outlive both a dismissal and an Activity recreation for the words to survive either.
     * Scoped to the navigation entry the feed already owns -- `viewModel(key = postId.value)` on the
     * Discover entry, exactly as the route scopes it today -- both hold. Scoped to this composable,
     * neither does.
     */
    onDismiss: () -> Unit,
    onRefresh: () -> Unit,
    onLoadMore: () -> Unit,
    onDraftChange: (String) -> Unit,
    onSend: () -> Unit,
    /** The state the heart should BE, never a flip. See `DiscoverPostDetailSource.setCommentLiked`. */
    onSetCommentLiked: (DiscoverComment, Boolean) -> Unit,
    onReply: (DiscoverComment) -> Unit,
    onCancelReply: () -> Unit,
    onBeginEditComment: (DiscoverComment) -> Unit,
    onEditDraftChange: (String) -> Unit,
    onSaveEdit: () -> Unit,
    onCancelEdit: () -> Unit,
    onDeleteComment: (DiscoverComment) -> Unit,
    onAcceptAnswer: (DiscoverComment) -> Unit,
    onClearAcceptedAnswer: () -> Unit,
    /** Opens somebody's public profile by username, the route `Navigation.kt` already owns. */
    onOpenProfile: (String) -> Unit,
    onDismissError: () -> Unit,
    onDismissNotice: () -> Unit,
    modifier: Modifier = Modifier,
    /**
     * SPS §16.3's report, for a `discover_comment` target.
     *
     * Null when reporting is not wired on this build, and every Report entry then disappears rather
     * than becoming inert. Whether a GIVEN comment may be reported is the separate question
     * `CommentRowActions.canReport` answers, and both have to be true.
     */
    onReport: ((ReportRequest) -> Unit)? = null,
    /**
     * The signed-in reader, for the disc at the left of the composer.
     *
     * NULLABLE, AND NULL DRAWS NO DISC RATHER THAN A PLACEHOLDER ONE. `PostDetailViewModel` holds
     * only a `UserId`: it asks ownership questions with it and has never needed a name or a photo,
     * and giving it a profile source to satisfy one avatar would put a network dependency into a
     * screen that had none. So the identity is passed in by the host, and a host that does not have
     * it yet gets a composer with no disc instead of a grey circle asserting somebody exists.
     */
    author: CommentComposerAuthor? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    // Read once per composition rather than per row: every timestamp in the sheet resolves against
    // the same "now", or two comments can disagree about whether it is still today. `ChatScreen`,
    // `ChatListScreen`, `DiscoverScreen` and `PostDetailScreen` all take the same reading.
    val now = remember { Clock.System.now() }

    // THE COMMENT'S ID AND NOT THE COMMENT. `rememberSaveable` has to be able to write this into a
    // Bundle on Android, and `DiscoverComment` is a domain object with no saver; a plain `remember`
    // would lose the open confirmation on rotation and leave a delete half-asked. The row is looked
    // up again below, which also means a comment deleted out from under the dialog closes it rather
    // than confirming against a ghost.
    var confirmDeleteId by rememberSaveable { mutableStateOf<String?>(null) }

    Dialog(
        onDismissRequest = onDismiss,
        // `swetSheetDialogProperties` RATHER THAN `DialogProperties`, and the difference is the
        // keyboard. A dialog window resizes itself over the keyboard on Android, and the composer
        // below applies `imePadding` as well, so the inset was paid twice: the surface measured
        // taller than the box it is bottom-aligned in and its handle and header were pushed off the
        // top of the screen. See the note on that function -- it is a defect only a real keyboard
        // on a real device can show.
        properties = swetSheetDialogProperties(
            // The back gesture and the scrim, which are two of the three ways out the user asked
            // for. The third is the ✕ in the header.
            dismissOnBackPress = true,
            dismissOnClickOutside = true,
            // A sheet is full-bleed. The platform default would inset it like an alert box and
            // leave a black gutter down both sides of the mockup's edge-to-edge surface.
            usePlatformDefaultWidth = false,
        ),
    ) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.BottomCenter) {
            // THE SCRIM'S TAP TARGET, AND IT HAS TO BE DRAWN BY HAND. `dismissOnClickOutside` means
            // "outside the dialog WINDOW", and `usePlatformDefaultWidth = false` makes that window
            // the whole screen: there is no outside left for it to fire on. Without this box the
            // second of the user's three ways out would silently not exist, and nothing about the
            // screen would look wrong. The dim itself is still the platform's.
            //
            // `pointerInput` rather than `swetPressable`: a scrim is not a button. It must not
            // appear in the accessibility tree as a control, must not take a press state layer, and
            // must not announce a role -- a screen reader user closes this with the ✕.
            Box(
                Modifier
                    .fillMaxSize()
                    .pointerInput(onDismiss) { detectTapGestures { onDismiss() } },
            )

            CommentsSheetSurface(
                state = state,
                now = now,
                zone = zone,
                onDismiss = onDismiss,
                onRefresh = onRefresh,
                onLoadMore = onLoadMore,
                onDraftChange = onDraftChange,
                onSend = onSend,
                onSetCommentLiked = onSetCommentLiked,
                onReply = onReply,
                onCancelReply = onCancelReply,
                onBeginEditComment = onBeginEditComment,
                onEditDraftChange = onEditDraftChange,
                onSaveEdit = onSaveEdit,
                onCancelEdit = onCancelEdit,
                onRequestDeleteComment = { confirmDeleteId = it.id.value },
                onAcceptAnswer = onAcceptAnswer,
                onClearAcceptedAnswer = onClearAcceptedAnswer,
                onOpenProfile = onOpenProfile,
                onDismissError = onDismissError,
                onDismissNotice = onDismissNotice,
                onReport = onReport,
                author = author,
                modifier = modifier,
            )
        }
    }

    // Outside the `Dialog`, so the confirmation is its own window rather than a box drawn inside
    // another one. Two stacked dialogs is what every other destructive action in this app does.
    val pending = confirmDeleteId?.let { id -> state.comments.firstOrNull { it.id.value == id } }
    if (confirmDeleteId != null && pending == null) {
        // The row went away while the question was on screen. Nothing to confirm, and nothing to
        // apologise for -- the outcome the user asked for already happened.
        LaunchedEffect(confirmDeleteId) { confirmDeleteId = null }
    }
    pending?.let { comment ->
        ProfileConfirmDialog(
            title = stringResource(Res.string.discover_groups_delete_comment_confirm_title),
            body = stringResource(Res.string.discover_groups_delete_comment_confirm_body),
            confirmText = stringResource(Res.string.discover_groups_delete),
            onConfirm = {
                confirmDeleteId = null
                onDeleteComment(comment)
            },
            onDismiss = { confirmDeleteId = null },
            busy = state.busy,
        )
    }
}

/**
 * Who the composer's disc belongs to.
 *
 * A pair rather than the domain `User`, because that type carries presence and a bio this surface
 * must not read: `SwetAvatar` would happily draw an online dot from it, and a reader's own dot on
 * their own composer is a claim about themselves that nobody asked for.
 */
data class CommentComposerAuthor(val displayName: String, val avatarUrl: String?)

/**
 * How much of the screen the sheet claims when the thread is long.
 *
 * A FRACTION AND NOT A HEIGHT, so it is right on a small phone and on a tablet. The remaining sixth
 * is what keeps the mockup's promise that the feed stays visible above: a sheet that filled the
 * window would be the pushed route again, wearing a drag handle.
 */
private const val SHEET_HEIGHT_FRACTION = 0.85f

/** How much of the visible list the thread's empty state takes. `PostDetailScreen`'s number. */
private const val EMPTY_STATE_HEIGHT_FRACTION = 0.4f

/** Enough placeholder rows to fill the first screen without pretending to know the real count. */
private const val SKELETON_ROWS = 4

private const val PAGING_FOOTER_KEY = "comments-paging-footer"
private const val EMPTY_ITEM_KEY = "comments-thread-empty"

/**
 * The sheet's own surface: handle, header, banners, the thread, the composer.
 *
 * Split out of [CommentsSheet] so the `Dialog` above contains one call rather than a screen, which
 * is `ReportSheet`'s split and for the same reason: the window's rules and the surface's layout are
 * two different subjects.
 *
 * `surface` RATHER THAN THE HOUSE CANVAS, and it is the one place in this pass that is not black.
 * The chat and chat-list re-skins put their screens on `background.deep` because a screen has
 * nothing behind it; this has the feed behind it, dimmed to nearly black by the dialog's scrim, and
 * a black sheet on a blackened feed has no edge at all. `surface` is SDS §4.3's slot for exactly
 * this ("Main surface: sheets, nav bars") and is the one layer above the canvas.
 */
@Composable
private fun CommentsSheetSurface(
    state: PostDetailUiState,
    now: Instant,
    zone: TimeZone,
    onDismiss: () -> Unit,
    onRefresh: () -> Unit,
    onLoadMore: () -> Unit,
    onDraftChange: (String) -> Unit,
    onSend: () -> Unit,
    onSetCommentLiked: (DiscoverComment, Boolean) -> Unit,
    onReply: (DiscoverComment) -> Unit,
    onCancelReply: () -> Unit,
    onBeginEditComment: (DiscoverComment) -> Unit,
    onEditDraftChange: (String) -> Unit,
    onSaveEdit: () -> Unit,
    onCancelEdit: () -> Unit,
    onRequestDeleteComment: (DiscoverComment) -> Unit,
    onAcceptAnswer: (DiscoverComment) -> Unit,
    onClearAcceptedAnswer: () -> Unit,
    onOpenProfile: (String) -> Unit,
    onDismissError: () -> Unit,
    onDismissNotice: () -> Unit,
    onReport: ((ReportRequest) -> Unit)?,
    author: CommentComposerAuthor?,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors

    Column(
        modifier = modifier
            .fillMaxWidth()
            .fillMaxHeight(SHEET_HEIGHT_FRACTION)
            // §7.2's `radius.xl` on the two corners that meet the feed. The bottom two are off the
            // screen, and rounding them would put two slivers of scrim under the composer.
            .clip(RoundedCornerShape(topStart = Radius.xl, topEnd = Radius.xl))
            .background(colors.surface)
            // SWALLOWS TAPS THAT LAND ON THE SHEET'S OWN BACKGROUND. The scrim above is a sibling
            // underneath this in the same `Box`, and a tap on bare sheet would otherwise fall
            // through to it and close the sheet the reader is using. Empty on purpose: it is a
            // barrier, not an action, so it adds nothing to the accessibility tree.
            .pointerInput(Unit) { detectTapGestures { } }
            // The horizontal insets only. The composer applies the bottom one itself, for the
            // reason `ChatScreen`'s does: `navigationBarsPadding` consumes what it applies, and
            // padding the whole sheet would lift the composer's own fill off the gesture bar and
            // show a stripe of scrim under it.
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Horizontal)),
    ) {
        SheetDragHandle()

        CommentsSheetHeader(
            count = state.post?.commentCount ?: state.comments.size,
            onDismiss = onDismiss,
        )

        // The freshness admission, and the Refresh that answers it. THE SAME COMPONENT the feed and
        // the group feed print, over the same `lastLoadedAt`: nothing about a Discover thread
        // arrives on its own -- `outbox.conversation_id` is NOT NULL and a comment has no
        // conversation -- so this line is the only statement on the sheet of how old the rows are.
        // It replaces the route's pull-to-refresh, which inside a sheet competes with the drag
        // gesture that closes it.
        FreshnessStrip(
            lastLoadedAt = state.lastLoadedAt,
            refreshing = state.refreshing,
            onRefresh = onRefresh,
        )

        // ABOVE THE CONTENT, NEVER IN PLACE OF IT. `ChatListScreen`'s and `ChatScreen`'s rule and
        // `SwetErrorBanner`'s reason for existing: a failed refresh must not blank a thread the
        // reader already has.
        state.error?.let {
            SwetErrorBanner(message = it.resolve(), onRetry = onRefresh, onDismiss = onDismissError)
        }
        // No Retry: a refused like is retried by tapping the heart again, and a Retry button in a
        // banner would be a second control for one gesture.
        state.notice?.let {
            SwetErrorBanner(message = it.resolve(), onDismiss = onDismissNotice)
        }

        when {
            // The post is gone. Every control below is over something that no longer exists, so the
            // sheet says the ONE sentence the server's single 404 supports and does not guess
            // between "deleted", "never existed", "in a group you left" and "written by somebody you
            // blocked" -- which is exactly what that one 404 refuses to distinguish.
            state.unavailable -> SwetEmptyState(
                title = stringResource(Res.string.discover_groups_post_unavailable_title),
                body = stringResource(Res.string.discover_groups_post_unavailable_body),
                actionText = stringResource(Res.string.comments_close),
                onAction = onDismiss,
                modifier = Modifier.weight(1f),
            )

            // NO POST YET. Skeletons rather than a spinner (SDS §2.1: avoid loading screens), and
            // `post == null` rather than `isFirstLoad` on purpose: a FAILED first read leaves
            // `loadedOnce` true with no post, and falling through to the list would print "No
            // comments yet." over a thread nobody managed to read. That false empty state is a
            // defect this project has already fixed once. The banner above says what happened.
            state.post == null -> Column(Modifier.weight(1f)) {
                repeat(SKELETON_ROWS) { SwetListRowSkeleton(avatarSize = Sizes.avatarComment) }
            }

            else -> LazyColumn(Modifier.weight(1f)) {
                commentThreadItems(
                    state = state,
                    now = now,
                    zone = zone,
                    onLoadMore = onLoadMore,
                    onSetCommentLiked = onSetCommentLiked,
                    onReply = onReply,
                    onBeginEditComment = onBeginEditComment,
                    onEditDraftChange = onEditDraftChange,
                    onSaveEdit = onSaveEdit,
                    onCancelEdit = onCancelEdit,
                    onDeleteComment = onRequestDeleteComment,
                    onAcceptAnswer = onAcceptAnswer,
                    onClearAcceptedAnswer = onClearAcceptedAnswer,
                    onOpenProfile = onOpenProfile,
                    onReport = onReport,
                )
            }
        }

        // GATED ON THERE BEING SOMETHING TO COMMENT ON, which is the same gate the route applies by
        // drawing its composer only in the loaded branch. A pill inviting somebody to write under a
        // post that is gone, or under four skeleton rows, is an affordance for a request the view
        // model would refuse -- `sendComment` returns early on `unavailable` -- and the refusal
        // would arrive as a banner after the words were typed.
        if (state.post != null && !state.unavailable) {
            CommentComposerBar(
                draft = state.draft,
                draftState = state.draftState,
                sending = state.sending,
                onDraftChange = onDraftChange,
                onSend = onSend,
                author = author,
                replyingTo = state.replyingTo,
                onCancelReply = onCancelReply,
            )
        }
    }
}

// -------------------------------------------------------------------------------------------
// The thread, written once
// -------------------------------------------------------------------------------------------

/**
 * The comment list: an empty state, the rows, and the paging footer. THE ONLY COPY.
 *
 * A `LazyListScope` extension rather than a composable, because the two callers put it in DIFFERENT
 * lists. The sheet's list contains only this; the route's contains the post card and the Message
 * action above it, in one `LazyColumn` so that a 1000-character post and an open keyboard can
 * scroll together. Returning a composable would have forced a nested scroller into one of them,
 * which is the defect `ReportSheet` records against its own reason list.
 *
 * THE POST IS NOT HERE, on purpose. In the sheet it is the card behind the scrim; on the route it is
 * an item the route adds. Putting it inside this function would mean the sheet drew the post twice
 * on one screen, four dp apart.
 */
internal fun LazyListScope.commentThreadItems(
    state: PostDetailUiState,
    now: Instant,
    zone: TimeZone,
    onLoadMore: () -> Unit,
    onSetCommentLiked: (DiscoverComment, Boolean) -> Unit,
    onReply: (DiscoverComment) -> Unit,
    onBeginEditComment: (DiscoverComment) -> Unit,
    onEditDraftChange: (String) -> Unit,
    onSaveEdit: () -> Unit,
    onCancelEdit: () -> Unit,
    /** Asks the caller to CONFIRM, never to delete. The confirmation is the host's, and it is not
     * optional -- SDS §11.1 requires one for a destructive action. */
    onDeleteComment: (DiscoverComment) -> Unit,
    onAcceptAnswer: (DiscoverComment) -> Unit,
    onClearAcceptedAnswer: () -> Unit,
    onOpenProfile: (String) -> Unit,
    onReport: ((ReportRequest) -> Unit)?,
) {
    val editingComment = state.edit as? DiscoverEdit.Comment

    if (state.comments.isEmpty()) {
        item(key = EMPTY_ITEM_KEY) {
            // SDS §17.1's voice in the design system's own empty state rather than a second one
            // written here. `fillParentMaxHeight` is what makes that reuse legal inside a lazy
            // list: `SwetEmptyState` fills the space it is given, and inside an item that space is
            // otherwise unbounded.
            SwetEmptyState(
                title = stringResource(Res.string.discover_groups_thread_empty_title),
                body = stringResource(Res.string.discover_groups_thread_empty_body),
                modifier = Modifier.fillParentMaxHeight(EMPTY_STATE_HEIGHT_FRACTION),
            )
        }
    }

    items(
        items = state.comments,
        // The comment id, never the position: a delete takes a row out of the middle, and a
        // positional key animates the wrong one away.
        key = { it.id.value },
    ) { comment ->
        CommentRow(
            comment = comment,
            // Null when the date could not be parsed, and NO EDIT MARK -- `comment.wasEdited` is
            // derived from `updated_at != created_at` and the deployed server sets those a
            // microsecond apart on about one row in ten at INSERT time. `CommentRow`'s header
            // carries the measurement and names where the fix belongs.
            timestamp = comment.createdAt?.let { discoverTimestamp(it, now, zone).resolve() },
            actions = state.actionsFor(comment),
            onSetLiked = { liked -> onSetCommentLiked(comment, liked) },
            onReply = { onReply(comment) },
            onEdit = { onBeginEditComment(comment) },
            onDelete = { onDeleteComment(comment) },
            canSelectAsAnswer = state.post?.let {
                it.type == com.swetdot.swetgram.domain.DiscoverPostType.QUESTION && state.actions.canDelete
            } == true,
            onSetAccepted = { accepted ->
                if (accepted) onAcceptAnswer(comment) else onClearAcceptedAnswer()
            },
            onOpenAuthor = { onOpenProfile(comment.author.username) },
            onReport = onReport?.let { report -> { report(commentReportRequest(comment)) } },
            // §19.4 gives list-item motion to insertion and removal, not to scrolling.
            modifier = swetAnimateItem(),
            editor = editingComment?.takeIf { it.id == comment.id }?.let { edit ->
                {
                    CommentInlineEditor(
                        draft = edit.draft,
                        saving = state.savingEdit,
                        canSave = commentDraftState(edit.draft).canSend,
                        onDraftChange = onEditDraftChange,
                        onSave = onSaveEdit,
                        onCancel = onCancelEdit,
                    )
                }
            },
        )
    }

    if (state.hasMore) {
        item(key = PAGING_FOOTER_KEY) {
            // Keyed on the item count, so it fires once per page rather than on every recomposition
            // -- and NOT again after a page that failed, because the count did not change. The
            // banner's Retry is the way back.
            LaunchedEffect(state.comments.size) { onLoadMore() }
            SwetListRowSkeleton(avatarSize = Sizes.avatarComment)
        }
    }
}

/**
 * What a report against one comment says. Pure, so the target and the account travel together.
 *
 * IT WAS BUILT AT THE CALL SITE, twice over once the sheet existed, and the account block is the
 * part that would have drifted: a `ReportRequest` whose `target` is the comment and whose `account`
 * is somebody else is a report filed against the wrong person, and nothing downstream would catch
 * it.
 */
internal fun commentReportRequest(comment: DiscoverComment): ReportRequest = ReportRequest(
    target = ReportTarget.Comment(comment.id),
    account = ReportedAccount(
        userId = comment.author.id,
        username = comment.author.username,
        displayName = comment.author.displayName,
    ),
)

// -------------------------------------------------------------------------------------------
// The row's overflow menu
// -------------------------------------------------------------------------------------------

/**
 * What a comment's «···» contains, as data.
 *
 * PURE AND SEPARATE FROM THE COMPOSABLE, exactly as `PostOverflowEntry` is for the post's overflow
 * and for the reason recorded there: the defect that pattern replaced was a menu that opened
 * containing one item, which no assertion in the suite could see. It also makes "is there a menu at
 * all" one expression, and an overflow button that opens an empty sheet is worse than no button.
 *
 * ORDER IS DELIBERATE and Report is last, because it is destructive and a destructive entry at the
 * top of a sheet is the one a thumb lands on when the sheet appears under it. Edit and Delete are
 * the author's; Report is everybody else's; `CommentRowActions` makes them complements of ownership,
 * so no row ever shows all three.
 *
 * THE LABELS ARE THE KEYS THE ROW ALREADY USED. These three actions were inline text buttons before
 * the mockup moved them behind a glyph, and they keep `discover_groups_edit`, `_delete` and
 * `_report` rather than gaining sheet-flavoured twins.
 */
internal enum class CommentOverflowEntry(val label: StringResource, val destructive: Boolean) {
    Edit(Res.string.discover_groups_edit, destructive = false),

    /**
     * §4.6's `danger`: "Delete, report, destructive actions".
     *
     * `CommentRowActions.canDelete` is true only for the comment's own author, and that guard is
     * load-bearing rather than cosmetic: `deleteDiscoverComment` addresses the row `(id, author_id)`
     * and answers **204** whatever happens, so an entry drawn on somebody else's comment would
     * report success and remove nothing.
     */
    Delete(Res.string.discover_groups_delete, destructive = true),

    /** SPS §16.3 names `discover_comment` among the five reportable things. */
    Report(Res.string.discover_groups_report, destructive = true),
}

/**
 * [CommentOverflowEntry] for one row.
 *
 * [reportWired] is the separate question of whether this BUILD has a report submitter behind it, and
 * it is deliberately not folded into `CommentRowActions`: that type answers "would the server accept
 * this from this viewer", which is true on a build that cannot send it. Both have to hold.
 */
internal fun commentOverflowEntries(
    actions: CommentRowActions,
    reportWired: Boolean,
): List<CommentOverflowEntry> = buildList {
    if (actions.canEdit) add(CommentOverflowEntry.Edit)
    if (actions.canDelete) add(CommentOverflowEntry.Delete)
    if (actions.canReport && reportWired) add(CommentOverflowEntry.Report)
}

// -------------------------------------------------------------------------------------------
// Editing
// -------------------------------------------------------------------------------------------

/**
 * The inline editor over one comment.
 *
 * IN PLACE, not in a dialog. An edit is a change to a SPECIFIC piece of text, and lifting it into a
 * modal takes away the one thing that says which piece -- the author line and the thread around it.
 * The author block above stays visible for the same reason.
 *
 * INPUT IS REFUSED PAST THE LIMIT rather than truncated on save, which is `ChatScreen`'s composer's
 * rule: silently truncating on send would lose the tail with no explanation. The bound is
 * [withinDraftLimit], which checks BOTH counts.
 */
@Composable
private fun CommentInlineEditor(
    draft: String,
    saving: Boolean,
    canSave: Boolean,
    onDraftChange: (String) -> Unit,
    onSave: () -> Unit,
    onCancel: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val focus = remember { FocusRequester() }

    // THE EDITOR OPENS WITH THE KEYBOARD UP. It was opened by an explicit "Edit", so there is
    // nothing else on screen the reader could have meant, and SDS §11.3 asks for the same wherever
    // a surface's only purpose is its input. Carried over from `PostDetailScreen`'s own editor
    // deliberately: that screen draws THIS editor now, and dropping the focus request would have
    // been a silent behaviour regression on a route nobody was looking at.
    LaunchedEffect(Unit) { focus.requestFocus() }

    Column(Modifier.fillMaxWidth()) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .defaultMinSize(minHeight = TouchTarget.recommended)
                .clip(RoundedCornerShape(Radius.field))
                .background(colors.card)
                .padding(horizontal = Space.two, vertical = Space.one + Space.half),
        ) {
            SwetBasicTextField(
                value = draft,
                onValueChange = {
                    if (withinDraftLimit(it, DiscoverContent.COMMENT_MAX)) onDraftChange(it)
                },
                enabled = !saving,
                textStyle = MaterialTheme.typography.bodyMedium.copy(color = colors.textPrimary),
                cursorBrush = SolidColor(colors.accent),
                modifier = Modifier.fillMaxWidth().focusRequester(focus),
            )
        }

        Spacer(Modifier.height(Space.half))

        Row(verticalAlignment = Alignment.CenterVertically) {
            // Cancel first and Save last, so the control that discards the draft is not the one
            // under the thumb at the end of a row read left to right.
            SwetTextButton(
                text = stringResource(Res.string.discover_groups_cancel),
                onClick = onCancel,
                enabled = !saving,
            )
            Spacer(Modifier.width(Space.one))
            SwetTextButton(
                text = stringResource(Res.string.discover_groups_save),
                onClick = onSave,
                enabled = canSave && !saving,
            )
        }
    }
}

// -------------------------------------------------------------------------------------------
// Chrome
// -------------------------------------------------------------------------------------------

/**
 * The mockup's small grey bar at the top centre.
 *
 * DECORATIVE, AND CLEARED FROM THE ACCESSIBILITY TREE. It is a grab affordance for a drag gesture,
 * and this sheet has no drag gesture: dismissal is the ✕, the scrim and the back gesture, which is
 * three ways out and all of them reachable without a pointer. Announcing a handle that drags
 * nothing would promise an interaction that is not there. It is drawn because the mockup draws it
 * and because it is what tells a sighted reader that this layer is dismissible at all.
 *
 * 36dp by 4dp: [Space.four] + [Space.half] wide and [Space.half] tall, which is the spacing scale
 * doing the arithmetic rather than a pair of free numbers.
 */
@Composable
private fun SheetDragHandle() {
    val colors = SwetgramThemeTokens.colors
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = Space.one)
            .clearAndSetSemantics { },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            Modifier
                .size(width = Space.four + Space.half, height = Space.half)
                .clip(RoundedCornerShape(Radius.full))
                // `border.strong` rather than `text.tertiary`: it is a piece of chrome, not text,
                // and §4.5 gives the strong border to exactly this kind of separator-weight shape.
                .background(colors.borderStrong),
        )
    }
}

/**
 * «Комментарии» with the count beside it, and the ✕ at the right.
 *
 * THE TITLE AND THE NUMERAL ARE ONE NODE FOR A SCREEN READER and that node says
 * `discover_comment_count` -- "3 comments" -- rather than "Comments" followed by a loose "3". The
 * numeral is drawn as a bare figure because that is what the mockup shows and because it is a
 * VALUE; the sentence a reader hears is the plural, which is the one place the one-versus-many rule
 * lives. Russian has three integer forms and nothing here may branch on `count == 1`.
 *
 * THE COUNT IS THE SERVER'S `comment_count`, which can legitimately exceed the rows below it: there
 * may be another page, and the view model adjusts it locally by one when the reader comments or
 * deletes. `FreshnessStrip` underneath says how old that statement is.
 *
 * `titleLarge` is 20sp/SemiBold, §5.3's `headline` -- "screen headers, card titles". The mockup asks
 * for "bold white ~20sp"; this is the existing rung at that size rather than a weight invented
 * between two of them.
 */
@Composable
private fun CommentsSheetHeader(count: Int, onDismiss: () -> Unit) {
    val colors = SwetgramThemeTokens.colors
    val spoken = commentCountLabel(count).resolve()

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.recommended)
            .padding(start = Space.screenMargin, end = Space.one, bottom = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier
                .weight(1f)
                // One node, one sentence. Without this a reader hears the word and then the digit,
                // which is the failure `SwetUnreadBadge` documents: a number alone is not what the
                // number means.
                .clearAndSetSemantics { contentDescription = spoken },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = stringResource(Res.string.comments_title),
                style = MaterialTheme.typography.titleLarge,
                color = colors.textPrimary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f, fill = false),
            )
            Spacer(Modifier.width(Space.one))
            Text(
                text = count.toString(),
                style = MaterialTheme.typography.titleLarge,
                color = colors.textSecondary,
                maxLines = 1,
            )
        }

        SwetIconButton(
            contentDescription = stringResource(Res.string.comments_close),
            onClick = onDismiss,
        ) { tint ->
            // The design system's own ✕, not a fourth hand-drawn one.
            SwetIcon(icon = SwetIcons.Close, tint = tint, contentDescription = null)
        }
    }
}

// -------------------------------------------------------------------------------------------
// The composer
// -------------------------------------------------------------------------------------------

/**
 * The mockup's pinned composer: the reader's disc, a pill, and a circular send button.
 *
 * SHARED WITH THE ROUTE once `PostDetailScreen` drops its private `Composer` -- see this file's
 * header. It is `internal` for that reason and not because anything outside this package needs it.
 *
 * THE DRAFT LIVES IN THE VIEW MODEL rather than in a `remember` here, unlike `ChatScreen`'s, and
 * that is about `createDiscoverComment` carrying no idempotency: the operation declares no
 * `Idempotency-Key` and its body has no `client_id`, so a retry on a dropped response posts the
 * comment TWICE and nothing may retry it automatically. A send that fails must therefore leave the
 * words exactly where they were, and a draft held in composition would be cleared by the screen the
 * moment it handed the text over. `PostDetailViewModel.sendComment` clears it only on a 201. It is
 * also what makes the draft survive both a dismissal and a rotation.
 *
 * THE PLACEHOLDER IS `discover_groups_add_comment`, THE KEY THAT ALREADY EXISTED. The mockup reads
 * «Написать комментарий...»; the existing Russian is «Комментарий». A second key for one field is
 * the twin this pass refused everywhere else, and the difference between the two sentences is
 * length rather than meaning.
 *
 * THE COUNTER APPEARS NEAR THE LIMIT AND NOT BEFORE -- `DraftState.showCounter`, which records that
 * as a DECISION the folder does not cover: a counter that is always on turns a composer into a form.
 */
@Composable
internal fun CommentComposerBar(
    draft: String,
    draftState: DraftState,
    sending: Boolean,
    onDraftChange: (String) -> Unit,
    onSend: () -> Unit,
    author: CommentComposerAuthor?,
    replyingTo: DiscoverReplyTarget? = null,
    onCancelReply: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors

    Row(
        modifier = modifier
            .fillMaxWidth()
            .background(colors.surface)
            // Lifts the composer above the keyboard and the gesture bar. Without these the input
            // sits under the keyboard, which is unusable and only shows up on a device.
            .imePadding()
            .navigationBarsPadding()
            .padding(horizontal = Space.two, vertical = Space.one),
        verticalAlignment = Alignment.Bottom,
        horizontalArrangement = Arrangement.spacedBy(Space.one),
    ) {
        // Null draws nothing rather than a grey circle. See [CommentsSheet]'s `author`.
        author?.let {
            SwetAvatar(
                displayName = it.displayName,
                size = Sizes.avatarComment,
                surfaceColor = colors.surface,
                avatarUrl = it.avatarUrl,
                // No dot on your own disc. It would be a claim about yourself nobody asked for,
                // and `SwetAvatar` never draws a grey one -- see its note.
                isOnline = false,
                modifier = Modifier.clearAndSetSemantics { },
            )
        }

        Column(
            modifier = Modifier
                .weight(1f)
                // §6.3's comfortable target as a MINIMUM, not a size: the pill grows with a long
                // comment instead of scrolling one line under the reader's thumb.
                .defaultMinSize(minHeight = TouchTarget.recommended)
                // A PILL, the mockup's shape and the conversation composer's. At 48dp a 20dp corner
                // reads as a rounded box rather than a pill.
                .clip(RoundedCornerShape(Radius.full))
                .background(colors.card)
                .padding(horizontal = Space.two, vertical = Space.one),
        ) {
            replyingTo?.let { target ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Text(
                        text = stringResource(Res.string.discover_groups_replying_to, target.displayName),
                        style = MaterialTheme.typography.labelSmall,
                        color = colors.textSecondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    SwetTextButton(
                        text = stringResource(Res.string.discover_groups_cancel),
                        onClick = onCancelReply,
                        enabled = !sending,
                    )
                }
            }
            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.CenterStart) {
                if (draft.isEmpty()) {
                    Text(
                        text = stringResource(Res.string.discover_groups_add_comment),
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.textTertiary,
                    )
                }
                SwetBasicTextField(
                    value = draft,
                    onValueChange = {
                        if (withinDraftLimit(it, DiscoverContent.COMMENT_MAX)) onDraftChange(it)
                    },
                    enabled = !sending,
                    textStyle = MaterialTheme.typography.bodyMedium.copy(color = colors.textPrimary),
                    cursorBrush = SolidColor(colors.accent),
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }

        Column(horizontalAlignment = Alignment.End) {
            if (draftState.showCounter) {
                Text(
                    text = draftState.remaining.toString(),
                    // §5.3's `caption` -- "Timestamps, counters, helper text".
                    style = MaterialTheme.typography.labelSmall,
                    color = if (draftState.remaining < 0) colors.danger else colors.textSecondary,
                )
                Spacer(Modifier.height(Space.half))
            }
            // `discover_groups_publish` IS THE LABEL -- "Post" the verb, «Опубликовать» -- reused
            // from the text button this replaces rather than given a new key for the same action in
            // a new shape.
            //
            // ALWAYS PRESENT, DISABLED WHEN THERE IS NOTHING TO SEND, unlike the conversation
            // composer's disc, which animates itself in and out. The difference is the surroundings
            // rather than a change of mind: that row has a "+" disc on the other end holding its
            // height when the send disc leaves, and this row does not -- so an appearing disc would
            // move the pill and the reader's own avatar on the first character typed. The disabled
            // state is §25.1's, and it is honest here because the pill beside it is visibly empty.
            SwetSendButton(
                contentDescription = stringResource(Res.string.discover_groups_publish),
                enabled = draftState.canSend && !sending,
                onClick = onSend,
            )
        }
    }
}

// THE MOCKUP'S CIRCULAR SEND BUTTON IS `SwetSendButton` in `ui/design/SwetComponents.kt`.
//
// It was written here first, as a private `CommentSendButton`, with the arrow's geometry copied
// from `ChatScreen`'s private `SendButton` -- the same three lines at the same seven fractions on
// the same 24dp canvas, and nothing that would have reported the two drifting apart. It is one
// component now, and the press feedback is the conversation's `Scale` on both: §2.1 gives nothing
// the right to stand between the user and the message they just sent, and posting a comment is the
// same act on a smaller stage.
